/**
 * InspectionDateField
 *
 * Standardised date-picker: a Popover with shadcn Calendar inside a Field.
 * Manages its own open/close state — callers only deal with the value.
 */

import { format } from 'date-fns'
import { enUS, ptBR } from 'date-fns/locale'
import { CalendarIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Field, FieldContent, FieldLabel } from '@/components/ui/field'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface InspectionDateFieldProps {
	label?: string
	value: Date | undefined
	onChange: (date: Date | undefined) => void
	isDisabled?: boolean
	isDueDate?: boolean
}

export const InspectionDateField = ({
	label,
	value,
	onChange,
	isDisabled = false,
	isDueDate = false,
}: InspectionDateFieldProps) => {
	const { i18n } = useTranslation()
	const [open, setOpen] = useState(false)
	const dateLocale = i18n.language.startsWith('pt') ? ptBR : enUS
	const today = new Date()

	// Sem valor (ex.: criacao), assume a data de hoje. O display usa sempre a
	// prop `value`, entao um form.reset posterior reflete a data real do registro.
	useEffect(() => {
		if (!value) {
			onChange(today)
		}
	}, [value, onChange, today])

	return (
		<Field>
			{label && <FieldLabel>{label}</FieldLabel>}
			<FieldContent>
				<Popover open={open} onOpenChange={setOpen}>
					<PopoverTrigger asChild>
						<Button
							variant="outline"
							disabled={isDisabled}
							className={cn(
								'w-full min-w-40 justify-start text-left h-9 font-normal bg-white border-input hover:bg-white data-[state=open]:bg-white',
								!value && 'text-muted-foreground',
							)}
						>
							<CalendarIcon className="size-4" />
							{value ? format(value, 'PPP', { locale: dateLocale }) : label}
						</Button>
					</PopoverTrigger>
					<PopoverContent
						className="w-(--radix-popover-trigger-width) sm:w-auto p-0"
						align="start"
					>
						<Calendar
							mode="single"
							selected={value}
							onSelect={(date) => {
								onChange(date)
								setOpen(false)
							}}
							disabled={isDueDate ? { before: today } : { after: today }}
							locale={dateLocale}
						/>
					</PopoverContent>
				</Popover>
			</FieldContent>
		</Field>
	)
}
